/**
 * The app's one workout store, over Capacitor Preferences (plan D21), and the
 * hook that binds its actions to the outbox T11 runs.
 *
 * Kept apart from `workout-store.ts` because this file imports `src/native`
 * (Capacitor): the Node tests build their own store over a memory storage and
 * never load it.
 */
import type { KeyValueStorage } from "@repo/client/i18n";
import { useContext, useMemo } from "react";
import { OutboxContext } from "../../../data/outbox-runner";
import { preferences } from "../../../native";
import { workoutPrefsStore } from "../prefs";
import {
  createWorkoutOutbox,
  outboxPortOf,
  useWorkoutOutboxPort,
} from "./outbox-bridge";
import {
  createWorkoutActions,
  createWorkoutStore,
  systemClock,
  type WorkoutActionSettings,
  type WorkoutActions,
} from "./workout-store";

const preferencesStorage: KeyValueStorage = {
  getItem: (key) => preferences.get(key),
  removeItem: (key) => preferences.remove(key),
  setItem: (key, value) => preferences.set(key, value),
};

/** What the actions read of the device settings (`D209`, B7). */
export const workoutActionSettings: WorkoutActionSettings = {
  warmupRestSeconds: () => workoutPrefsStore.getState().warmupRestSeconds,
};

export const workoutStore = createWorkoutStore({
  onProblem: (report) => {
    console.error("[workout] stored session problem", report);
  },
  storage: preferencesStorage,
});

/**
 * The store's actions, each writing its outbox item through the runner in
 * `DataProvider` (so every enqueue triggers a drain). Must render inside it.
 */
export function useWorkoutActions(): WorkoutActions {
  const port = useWorkoutOutboxPort();
  return useMemo(
    () =>
      createWorkoutActions(
        workoutStore,
        createWorkoutOutbox(port),
        systemClock,
        workoutActionSettings
      ),
    [port]
  );
}

/**
 * {@link useWorkoutActions} for a component that only SOMETIMES acts on the
 * workout — the catalogue, whose workout door is one of several (`D254`):
 * `null` outside `DataProvider` instead of a throw, so a catalogue opened
 * from any other door, or drawn by a story with no outbox, never needs the
 * workout store.
 */
export function useOptionalWorkoutActions(): WorkoutActions | null {
  const runner = useContext(OutboxContext);
  return useMemo(
    () =>
      runner === null
        ? null
        : createWorkoutActions(
            workoutStore,
            createWorkoutOutbox(outboxPortOf(runner)),
            systemClock,
            workoutActionSettings
          ),
    [runner]
  );
}
